import { CLOSED_DEAL_STAGES, isClosingGoalMet, stageHasPrice } from "./arkaTokens";
import { dealHasMonthly } from "./arkaCatalog";
import type { Deal, Task } from "@/components/atomic-crm/types";

export type DashboardPeriod = "week" | "month" | "all";

export interface CurrencyBucket {
  currency: string;
  pipeline: number;
  won: number;
  mrr: number;
  wonCount: number;
}

export interface DashboardMetrics {
  period: DashboardPeriod;
  openCount: number;
  onboardingCount: number;
  activeCount: number;
  wonCount: number;
  lostCount: number;
  winRate: number;
  velocityDays: number | null;
  pendingOverdue: number;
  pendingToday: number;
  pendingAll: number;
  newDeals: number;
  onTimeCount: number;
  lateCount: number;
  onTimeRate: number | null;
  byCurrency: Record<"COP" | "USD", CurrencyBucket>;
  byStage: Record<string, { count: number; amountByCurrency: Record<string, number> }>;
  byFamily: Record<string, { count: number; wonCop: number; wonUsd: number }>;
  byProduct: Record<string, { count: number; wonCop: number; wonUsd: number }>;
}

const REVENUE_STAGES = new Set(["won", "onboarding", "active", "repurchase"]);
const OPEN_EXCLUDED_STAGES = new Set([
  "won",
  "lost",
  "onboarding",
  "active",
  "repurchase",
]);

export function getDealCurrency(deal: Pick<Deal, "currency">): "COP" | "USD" {
  return deal.currency === "USD" ? "USD" : "COP";
}

function startOfPeriod(period: DashboardPeriod, now: Date): Date | null {
  if (period === "all") {
    return null;
  }
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (period === "week") {
    const weekday = start.getDay();
    const offset = weekday === 0 ? 6 : weekday - 1;
    start.setDate(start.getDate() - offset);
    return start;
  }
  start.setDate(1);
  return start;
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
}

function isInPeriod(
  value: string | null | undefined,
  periodStart: Date | null,
  now: Date,
): boolean {
  if (periodStart == null) {
    return true;
  }
  const date = parseDate(value);
  if (date == null) {
    return false;
  }
  return date >= periodStart && date <= now;
}

function emptyBucket(currency: "COP" | "USD"): CurrencyBucket {
  return { currency, pipeline: 0, won: 0, mrr: 0, wonCount: 0 };
}

export function dealAmount(deal: Pick<Deal, "amount">): number {
  return Number.isFinite(deal.amount) ? deal.amount : 0;
}

export function dealMonthlyAmount(deal: Pick<Deal, "monthly_amount">): number {
  return Number.isFinite(deal.monthly_amount) ? Number(deal.monthly_amount) : 0;
}

export function isPendingTask(task: Pick<Task, "done_date">): boolean {
  return task.done_date == null;
}

export function buildDashboardMetrics(
  deals: Deal[],
  tasks: Task[],
  period: DashboardPeriod,
  now = new Date(),
): DashboardMetrics {
  const periodStart = startOfPeriod(period, now);
  const byCurrency: Record<"COP" | "USD", CurrencyBucket> = {
    COP: emptyBucket("COP"),
    USD: emptyBucket("USD"),
  };
  const byStage: DashboardMetrics["byStage"] = {};
  const byFamily: DashboardMetrics["byFamily"] = {};
  const byProduct: DashboardMetrics["byProduct"] = {};

  let openCount = 0;
  let onboardingCount = 0;
  let activeCount = 0;
  let wonCount = 0;
  let lostCount = 0;
  let newDeals = 0;
  let velocityTotal = 0;
  let velocitySamples = 0;
  let onTimeCount = 0;
  let lateCount = 0;

  for (const deal of deals) {
    const currency = getDealCurrency(deal);
    const amount = dealAmount(deal);
    const family = deal.category || "other";
    const product = deal.product || family;

    if (byStage[deal.stage] == null) {
      byStage[deal.stage] = { count: 0, amountByCurrency: {} };
    }
    byStage[deal.stage].count += 1;
    if (stageHasPrice(deal.stage)) {
      byStage[deal.stage].amountByCurrency[currency] =
        (byStage[deal.stage].amountByCurrency[currency] ?? 0) + amount;
    }

    if (byFamily[family] == null) {
      byFamily[family] = { count: 0, wonCop: 0, wonUsd: 0 };
    }
    byFamily[family].count += 1;

    if (byProduct[product] == null) {
      byProduct[product] = { count: 0, wonCop: 0, wonUsd: 0 };
    }
    byProduct[product].count += 1;

    if (deal.stage === "onboarding") {
      onboardingCount += 1;
    } else if (deal.stage === "active" || deal.stage === "repurchase") {
      activeCount += 1;
    } else if (!CLOSED_DEAL_STAGES.has(deal.stage)) {
      openCount += 1;
    }

    if (isInPeriod(deal.created_at, periodStart, now)) {
      newDeals += 1;
    }

    if (stageHasPrice(deal.stage) && !OPEN_EXCLUDED_STAGES.has(deal.stage)) {
      byCurrency[currency].pipeline += amount;
    }

    const wonAt = deal.purchase_date ?? deal.updated_at;
    if (REVENUE_STAGES.has(deal.stage) && isInPeriod(wonAt, periodStart, now)) {
      byCurrency[currency].won += amount;
      byCurrency[currency].wonCount += 1;
      if (currency === "USD") {
        byFamily[family].wonUsd += amount;
        byProduct[product].wonUsd += amount;
      } else {
        byFamily[family].wonCop += amount;
        byProduct[product].wonCop += amount;
      }
      wonCount += 1;
      const start = parseDate(deal.created_at);
      const end = parseDate(wonAt);
      if (start && end) {
        velocityTotal += Math.max((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24), 0);
        velocitySamples += 1;
      }
      const goalMet = isClosingGoalMet(deal.expected_closing_date, deal.purchase_date);
      if (goalMet === true) {
        onTimeCount += 1;
      } else if (goalMet === false) {
        lateCount += 1;
      }
    }

    if (deal.stage === "lost" && isInPeriod(deal.updated_at, periodStart, now)) {
      lostCount += 1;
    }

    if (
      dealHasMonthly(deal.billing_type) &&
      REVENUE_STAGES.has(deal.stage)
    ) {
      byCurrency[currency].mrr += dealMonthlyAmount(deal);
    }
  }

  const closedCount = wonCount + lostCount;
  const pending = tasks.filter(isPendingTask);
  const startToday = new Date(now);
  startToday.setHours(0, 0, 0, 0);
  const endToday = new Date(startToday);
  endToday.setDate(endToday.getDate() + 1);

  let pendingOverdue = 0;
  let pendingToday = 0;
  for (const task of pending) {
    const due = parseDate(task.due_date);
    if (due == null) {
      continue;
    }
    if (due < startToday) {
      pendingOverdue += 1;
    } else if (due < endToday) {
      pendingToday += 1;
    }
  }

  const goalSamples = onTimeCount + lateCount;
  return {
    period,
    openCount,
    onboardingCount,
    activeCount,
    wonCount,
    lostCount,
    winRate: closedCount === 0 ? 0 : (wonCount / closedCount) * 100,
    velocityDays:
      velocitySamples === 0 ? null : Math.round(velocityTotal / velocitySamples),
    pendingOverdue,
    pendingToday,
    pendingAll: pending.length,
    newDeals,
    onTimeCount,
    lateCount,
    onTimeRate: goalSamples === 0 ? null : (onTimeCount / goalSamples) * 100,
    byCurrency,
    byStage,
    byFamily,
    byProduct,
  };
}

export function formatAmountsByCurrency(
  amountByCurrency: Record<string, number>,
  formatMoney: (amount: number, currency: string) => string,
): string {
  const parts = (["COP", "USD"] as const)
    .filter((currency) => (amountByCurrency[currency] ?? 0) > 0)
    .map((currency) => formatMoney(amountByCurrency[currency] ?? 0, currency));
  if (parts.length === 0) {
    return "";
  }
  return parts.join(" · ");
}
